import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { logoutUser } from "src/store/auth"
import { selectCredits, selectMyAvatar } from "src/store/economy"
import {
  linkGoogle,
  selectEmail,
  selectGoogleEmail,
  selectGoogleLinked,
  selectHasPassword,
  selectIsGuest,
  selectRankingPoints,
  selectUserId,
  selectUsername,
  unlinkGoogle,
} from "src/store/user"

import Modal from "src/components/modals/Modal"
import LanguageSwitcher from "src/components/LanguageSwitcher"
import AudioSettings from "src/components/AudioSettings"
import InstallSettings from "src/components/InstallSettings"
import PushSettings from "src/components/PushSettings"
import ShortcutSettings from "src/components/ShortcutSettings"
import { useHasKeyboard } from "src/utils/input"
import FogPreviewToggle from "src/components/FogPreviewToggle"
import GoogleSignInButton from "src/components/GoogleSignInButton"
import UsernameEditor from "src/components/UsernameEditor"
import DeleteAccount from "src/components/DeleteAccount"
import SaveGuestButton from "src/components/SaveGuestButton"
import MyAvatar from "src/components/MyAvatar"
import PlayerCardButton from "src/components/PlayerCardButton"
import AvatarPicker from "src/components/AvatarPicker"

import "./SettingsModal.css"

// Same grouping as the hall of fame on the landing page ("2 010 PTS"), so the player's score reads
// the same in the two places it is shown.
const formatPoints = (points) => String(points ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")

const SettingsModal = () => {
  const hasKeyboard = useHasKeyboard()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const rankingPoints = useSelector(selectRankingPoints)
  const userId = useSelector(selectUserId)
  const username = useSelector(selectUsername)
  const myAvatar = useSelector(selectMyAvatar)
  const email = useSelector(selectEmail)
  const googleLinked = useSelector(selectGoogleLinked)
  const googleEmail = useSelector(selectGoogleEmail)
  const hasPassword = useSelector(selectHasPassword)
  const credits = useSelector(selectCredits)
  const isGuest = useSelector(selectIsGuest)
  // A guest's address is made up and it has no password, so signing out is losing the account: the first press
  // says so and the second one means it (owner, 2026-10-02).
  const [confirmingGuestLogout, setConfirmingGuestLogout] = useState(false)

  const clickLogout = () => {
    if (isGuest && !confirmingGuestLogout) {
      setConfirmingGuestLogout(true)
      return
    }
    dispatch(logoutUser())
    ModalService.hideAll()
  }

  return (
    <Modal width="min(520px, 92vw)">
      <div className="profile">
        <header className="profile-head">
          {/* Your own card: the league, the place and what the day pays, as the others see it. */}
          <PlayerCardButton user={userId != null ? { id: userId, username, rankingPoints } : null} picture={myAvatar}>
            <MyAvatar className="profile-avatar" />
          </PlayerCardButton>
          <div className="profile-identity">
            <UsernameEditor />
            <p className="p-mini profile-points">{t("settings.points", { points: formatPoints(rankingPoints) })}</p>
          </div>
        </header>

        {/* A guest's way to keep the account, first thing on the profile. */}
        <SaveGuestButton className="profile-save-guest" />

        {/* The player's picture, shown in the ranking, the hall of fame and the header. */}
        <section className="profile-avatars">
          <p className="section-label">{t("settings.avatar")}</p>
          <AvatarPicker />
        </section>

        <dl className="profile-facts">
          <dt>{t("settings.email")}</dt>
          {/* A guest's address is the game's own, made from its name: not one to show as theirs. */}
          <dd>{isGuest ? "—" : email || "—"}</dd>
          {/* The profile is where a player comes to look themselves up, so the balance is here as
              well as in the header — and the way to the shop is beside it rather than somewhere
              else to be found. */}
          <dt>{t("settings.credits")}</dt>
          <dd className="profile-credits">
            <span>{credits === null ? "—" : formatPoints(credits)}</span>
            {/* The shop refuses a guest server-side; the door is not offered to one. */}
            {!isGuest && (
              <button type="button" className="quiet" onClick={() => ModalService.show("ShopModal")}>
                {t("settings.openShop")}
              </button>
            )}
          </dd>
          <dt>{t("settings.language")}</dt>
          <dd>
            <LanguageSwitcher />
          </dd>
          {/* Linking is the same one-string handoff as signing in, pointed at your own account
              (2026-08-21). Unlinking is refused server-side while no password backs the account,
              and the button says so rather than letting the player find out by being locked out.
              A guest saves the account with Google from the save sheet instead. */}
          {!isGuest && (
            <>
              <dt className="profile-google-label">Google</dt>
              <dd className="profile-google">
                {googleLinked ? (
                  <>
                    {/* Which account, not merely that there is one: a player with a personal and a
                        work address cannot answer "linked to what?" from a tick. */}
                    <span className="profile-google-state">
                      ✓{" "}
                      {googleEmail
                        ? t("settings.googleLinkedAs", { email: googleEmail })
                        : t("settings.googleLinkedState")}
                    </span>
                    <button
                      type="button"
                      className="quiet"
                      disabled={!hasPassword}
                      title={hasPassword ? undefined : t("settings.googleNeedsPassword")}
                      onClick={() => dispatch(unlinkGoogle())}
                    >
                      {t("settings.googleUnlink")}
                    </button>
                  </>
                ) : (
                  <GoogleSignInButton
                    text="continue_with"
                    divider={false}
                    onCredential={(credential) => dispatch(linkGoogle(credential))}
                  />
                )}
              </dd>
            </>
          )}
        </dl>

        {/* Rebinding keys is meaningless without keys. This is a capability question, not a width
            one: a tablet has a roomy screen and nothing to press, and a narrow window on a PC has
            a full keyboard — see utils/input, which asks about the keyboard rather than the size. */}
        {/* Music and effects, before the shortcuts: everybody has ears and only some have keys. */}
        <AudioSettings />
        {hasKeyboard && <ShortcutSettings />}
        {/* The banner can be waved away for a month; this is the door that stays open. */}
        <InstallSettings />
        {/* Round notifications: only where the browser, the worker and the server can all do it. */}
        <PushSettings />
        <FogPreviewToggle />

        {/* The two ways out of an account, and nothing else: the modal's own X is the way out of
            the modal, so a CLOSE button beside them was a third button saying what the corner
            already says. Deleting sits opposite leaving, behind a link of its own, and opens into
            the width above the row rather than beside it. */}
        {confirmingGuestLogout && (
          <p className="errored-text profile-guest-logout" role="alert">
            {t("guest.logoutWarning")}
          </p>
        )}
        <div className="profile-actions">
          <DeleteAccount />
          <button type="button" className="cancel" onClick={clickLogout}>
            {confirmingGuestLogout ? t("guest.logoutConfirm") : t("settings.logout")}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default SettingsModal
